/**
 * Three-Plume Visualization Component
 *
 * Renders PSH (LNAPL) and Dissolved plumes overlaid on a satellite map
 * with proper coupling physics visualization:
 * - PSH shrinks as it dissolves
 * - Dissolved plume grows from PSH dissolution
 *
 * Each plume uses concentration gradients (dark core → light edge)
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { MapContainer, Polygon, Circle, Marker, Tooltip, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { EsriVectorBasemap } from '@/lib/basemap';
import {
  ThreePlumeState,
  RecoveryWell,
  MDPEWell,
  SiteGradientMap,
  ContaminantResult,
  ContaminantBoundaryData,
  DWRecommendation,
  RemediationHotspot,
} from '../../api/polygon';
import { PlusIcon } from '@heroicons/react/24/outline';
import { GradientContourLayer, GradientLayerToggle } from './GradientContourLayer';
import { MultiContaminantPlumeLayer, PredictedWellConcentration } from './MultiContaminantPlumeLayer';
import { PSHCanvasLayer } from './PSHCanvasLayer';
import { usePSHData, PSHWellData } from './PSHVoxelCube';
import { MapCompass } from './MapCompass';
import { CtrlScrollZoom } from './CtrlScrollZoom';

// ============================================================================
// TYPES
// ============================================================================

export type WellPlacementMode = 'recovery' | 'mdpe' | null;

export interface LayerVisibility {
  psh: boolean;
  dissolved: boolean;
}

// Extracted plume boundary from AGMR document
export interface ExtractedPlumeBoundary {
  concentration_ug_l: number;
  boundary_coords: [number, number][]; // Array of [lat, lon]
}

// Monitoring well for display
export interface MonitoringWellMarker {
  id: string;
  well_id: string;
  latitude: number;
  longitude: number;
  depth_ft?: number;
}

export interface ThreePlumeMapProps {
  centerLat: number;
  centerLon: number;
  plumeStates: ThreePlumeState[];
  flowDirection?: number;
  initialMonth?: number;
  onMonthChange?: (month: number) => void;
  showControls?: boolean;
  autoPlay?: boolean;
  // Layer visibility (controlled externally when provided)
  visibleLayers?: LayerVisibility;
  onVisibleLayersChange?: (layers: LayerVisibility) => void;
  // Well support
  recoveryWells?: RecoveryWell[];
  mdpeWells?: MDPEWell[];
  monitoringWells?: MonitoringWellMarker[];  // Monitoring wells (display only)
  wellPlacementMode?: WellPlacementMode;
  onWellPlaced?: (type: 'recovery' | 'mdpe', lat: number, lon: number) => void;
  onWellRemoved?: (type: 'recovery' | 'mdpe', id: string) => void;
  // Gradient map support
  gradientMap?: SiteGradientMap | null;
  showGradientContours?: boolean;
  onGradientContoursToggle?: (show: boolean) => void;
  // Extracted plume boundaries (from AGMR extraction) - shown as reference at T=0
  extractedPlumeBoundaries?: ExtractedPlumeBoundary[] | null;
  showExtractedBoundaries?: boolean;
  onExtractedBoundariesToggle?: (show: boolean) => void;
  // Multi-contaminant plume support
  contaminantResults?: ContaminantResult[];
  showMultiContaminant?: boolean;
  visibleCompounds?: Set<string>;  // Which compounds are visible (undefined = all)
  // Well-based plume boundaries from API (overrides theoretical plumes)
  wellBoundaries?: ContaminantBoundaryData[];
  // v3.2 per-(well_name, analyte_code_lower) → predicted_conc_mgl override at
  // the user-selected horizon. Empty Map = no override (render measured data
  // as-is). When set, aggregatedWells replaces each well's measured
  // concentration with the forecast value for the chosen horizon.
  wellHorizonOverrides?: Map<string, Map<string, number>>;
  // Selected horizon in days (0 = now / measured, 90 / 365 / ... / 7300 for v3.2).
  horizonDays?: number;
  // Wells with exceedance (concentration above threshold)
  wellsAboveThreshold?: Array<{
    well_id: string;
    lat: number;
    lon: number;
    concentration: number;
  }>;
  // Physics engine predictions (for time slider integration)
  predictedWellConcentrations?: PredictedWellConcentration[];
  predictionDate?: string;
  // Site ID (used for PSH well lookup tooltips)
  siteId?: string;
  // Target date for PSH data (for historical timeline support)
  // If provided, fetches PSH data at or before this date
  pshTargetDate?: string | null;
  // DW (Delineation Well) recommendations for non-delineated directions
  dwRecommendations?: DWRecommendation[];
  // Remediation hotspots — wells with significantly elevated PSH or dissolved concentrations
  remediationHotspots?: RemediationHotspot[];
}

// ============================================================================
// CONSTANTS - Color gradients for each plume type
// ============================================================================

// PSH/LNAPL - Brown/Amber gradient (dark to light)
const PSH_COLORS = {
  core: '#78350f',      // Very dark brown (100% concentration)
  high: '#92400e',      // Dark brown (75%)
  medium: '#b45309',    // Medium brown (50%)
  low: '#d97706',       // Light brown (25%)
  outer: '#fbbf24',     // Very light amber (10%)
};

// Dissolved - Blue/Navy gradient (dark to light)
const DISSOLVED_COLORS = {
  core: '#1e3a5f',      // Very dark navy (100% concentration)
  high: '#1e40af',      // Dark blue (75%)
  medium: '#2563eb',    // Medium blue (50%)
  low: '#3b82f6',       // Light blue (25%)
  outer: '#93c5fd',     // Very light blue (10%)
};


// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if coordinates appear to be in relative 0-1 range vs absolute lat/lon.
 *
 * Relative coordinates: Values in 0-1 range representing position on map image
 * Absolute coordinates: Real GPS lat/lon (e.g., lat 25-50 for US, lon -125 to -65)
 */
function areCoordinatesRelative(coordinates: [number, number][]): boolean {
  if (!coordinates || coordinates.length === 0) return false;

  let allInRelativeRange = true;
  let anyLooksLikeRealLatLon = false;

  for (const [lat, lon] of coordinates) {
    // Check if values are clearly in relative 0-1 range
    if (lat < -0.1 || lat > 1.1 || lon < -0.1 || lon > 1.1) {
      allInRelativeRange = false;
    }

    // Check if values look like real-world lat/lon coordinates
    // US latitudes: ~25 to ~49 (lower 48) or ~18 to ~71 (including Alaska, Hawaii, PR)
    // US longitudes: ~-125 to ~-65 (lower 48) or ~-180 to ~-65 (including Alaska, Hawaii)
    const looksLikeLatitude = Math.abs(lat) > 10 && Math.abs(lat) <= 90;
    const looksLikeLongitude = Math.abs(lon) > 10 && Math.abs(lon) <= 180;

    if (looksLikeLatitude || looksLikeLongitude) {
      anyLooksLikeRealLatLon = true;
    }
  }

  // If any coordinate looks like real lat/lon, treat as absolute
  if (anyLooksLikeRealLatLon) {
    return false;
  }

  // If all coordinates are in 0-1 range, treat as relative
  return allInRelativeRange;
}

/**
 * Convert relative coordinates (0-1 range) to absolute lat/lon.
 */
function convertRelativeToAbsolute(
  relLat: number,
  relLon: number,
  centerLat: number,
  centerLon: number,
  radiusFt: number = 2000
): [number, number] {
  const ftPerDegLat = 364000;
  const ftPerDegLon = 364000 * Math.cos(centerLat * Math.PI / 180);

  const offsetFtLat = (relLat - 0.5) * 2 * radiusFt;
  const offsetFtLon = (relLon - 0.5) * 2 * radiusFt;

  const offsetDegLat = offsetFtLat / ftPerDegLat;
  const offsetDegLon = offsetFtLon / ftPerDegLon;

  return [centerLat + offsetDegLat, centerLon + offsetDegLon];
}

/**
 * Parse map_north_direction to rotation angle.
 */
function parseMapNorthDirection(direction?: string): number {
  if (!direction) return 0;
  const dir = direction.toLowerCase().trim();
  switch (dir) {
    case 'up': case 'n': case 'north': case '0': return 0;
    case 'up-right': case 'ne': case 'northeast': case '45': return -45;
    case 'right': case 'e': case 'east': case '90': return -90;
    case 'down-right': case 'se': case 'southeast': case '135': return -135;
    case 'down': case 's': case 'south': case '180': return -180;
    case 'down-left': case 'sw': case 'southwest': case '225': return -225;
    case 'left': case 'w': case 'west': case '270': return -270;
    case 'up-left': case 'nw': case 'northwest': case '315': return -315;
    default:
      const angle = parseFloat(dir);
      return !isNaN(angle) ? -angle : 0;
  }
}

/**
 * Rotate a coordinate around a center point.
 */
function rotateCoordinate(
  lat: number,
  lon: number,
  centerLat: number,
  centerLon: number,
  angleDeg: number
): [number, number] {
  if (angleDeg === 0) return [lat, lon];
  const angleRad = (angleDeg * Math.PI) / 180;
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);
  const dLat = lat - centerLat;
  const dLon = lon - centerLon;
  const rotatedDLat = dLat * cosA - dLon * sinA;
  const rotatedDLon = dLat * sinA + dLon * cosA;
  return [centerLat + rotatedDLat, centerLon + rotatedDLon];
}

/**
 * Convert an array of coordinates from relative to absolute if needed,
 * and optionally apply rotation based on map compass direction.
 */
function convertBoundaryCoords(
  coords: [number, number][],
  centerLat: number,
  centerLon: number,
  radiusFt: number = 2000,
  mapNorthDirection?: string
): [number, number][] {
  if (!coords || coords.length === 0) return coords;

  let processedCoords = coords;

  // Check if conversion is needed
  if (areCoordinatesRelative(coords)) {
    // Convert each coordinate from relative to absolute
    processedCoords = coords.map(([lat, lon]) =>
      convertRelativeToAbsolute(lat, lon, centerLat, centerLon, radiusFt)
    );
  }

  // Apply rotation if map north direction is not standard "up"
  const rotationAngle = parseMapNorthDirection(mapNorthDirection);
  if (rotationAngle !== 0) {
    processedCoords = processedCoords.map(([lat, lon]) =>
      rotateCoordinate(lat, lon, centerLat, centerLon, rotationAngle)
    );
  }

  return processedCoords;
}

// Convert area (sqft) to radius (ft)
/** Defensive concentration formatter for the well-label tooltip. Filters out
 *  the legacy "crazy numbers" cases — NaN, ±Infinity, negative, or absurdly
 *  large floats that leaked through when log10-space values got treated as
 *  raw concentrations. Switches to exponential notation past 999 mg/L so
 *  the layout doesn't break. */
function formatConcMgL(v: number): string {
  if (!Number.isFinite(v) || v < 0) return '—';
  if (v >= 1000) return v.toExponential(2);
  if (v >= 1) return v.toFixed(2);
  if (v >= 0.01) return v.toFixed(3);
  if (v >= 1e-4) return v.toFixed(5);
  return v === 0 ? '0.000' : v.toExponential(1);
}

function areaToRadius(areaSqft: number): number {
  return Math.sqrt(areaSqft / Math.PI);
}

// Convert feet to degrees latitude (approximate)
function feetToLatDegrees(feet: number): number {
  return feet / 364000;
}

// Convert feet to degrees longitude at a given latitude
function feetToLonDegrees(feet: number, lat: number): number {
  return feet / (364000 * Math.cos(lat * Math.PI / 180));
}

// Source location marker icon
const sourceIcon = L.divIcon({
  className: 'source-icon',
  html: `<div style="
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 0 12px rgba(239,68,68,0.6), 0 2px 8px rgba(0,0,0,0.4);
    z-index: 1000;
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Note: Well markers now use inline L.divIcon with dynamic labels
// showing the well ID (e.g., "MW-17") instead of static icons

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Component to add a scale bar (metric + imperial)
function MapScaleBar() {
  const map = useMap();
  useEffect(() => {
    const scale = L.control.scale({
      position: 'bottomleft',
      imperial: true,
      metric: true,
      maxWidth: 150,
    });
    scale.addTo(map);
    return () => { scale.remove(); };
  }, [map]);
  return null;
}

// Component to auto-fit bounds
function FitBounds({ bounds }: { bounds: L.LatLngBoundsExpression }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
}

// Component to handle map clicks for well placement
function MapClickHandler({
  mode,
  onPlace,
}: {
  mode: WellPlacementMode;
  onPlace: (lat: number, lon: number) => void;
}) {
  const map = useMap();

  useMapEvents({
    click: (e) => {
      if (mode) {
        onPlace(e.latlng.lat, e.latlng.lng);
      }
    },
  });

  // Change cursor when in placement mode
  useEffect(() => {
    if (mode) {
      map.getContainer().style.cursor = 'crosshair';
    } else {
      map.getContainer().style.cursor = '';
    }
    return () => {
      map.getContainer().style.cursor = '';
    };
  }, [mode, map]);

  return null;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ThreePlumeMap({
  centerLat,
  centerLon,
  plumeStates,
  flowDirection = 180,
  initialMonth = 0,
  onMonthChange,
  showControls = true,
  autoPlay = false,
  // Layer visibility (controlled externally when provided)
  visibleLayers: externalVisibleLayers,
  onVisibleLayersChange,
  // Well props
  recoveryWells = [],
  mdpeWells = [],
  monitoringWells = [],
  wellPlacementMode,
  onWellPlaced,
  onWellRemoved,
  // Gradient map props
  gradientMap,
  showGradientContours = true,
  onGradientContoursToggle,
  // Extracted plume boundaries props
  extractedPlumeBoundaries,
  showExtractedBoundaries = true,
  onExtractedBoundariesToggle,
  // Multi-contaminant plume props
  contaminantResults,
  showMultiContaminant = true,
  visibleCompounds,
  wellBoundaries,
  wellHorizonOverrides,
  horizonDays = 0,
  // Wells above threshold (exceedance markers)
  wellsAboveThreshold = [],
  // Physics engine predictions
  predictedWellConcentrations = [],
  predictionDate,
  // Site ID (used for PSH well lookup tooltips)
  siteId,
  // Target date for PSH data (historical timeline support)
  pshTargetDate,
  dwRecommendations = [],
  remediationHotspots = [],
}: ThreePlumeMapProps) {
  // If pshTargetDate is provided, fetches historical data at/before that date
  const { wells: pshWells } = usePSHData(siteId || null, pshTargetDate);

  // Initialize selectedIndex from initialMonth prop
  // initialMonth is the actual month value (time_months), we need to find the corresponding index
  const getIndexFromMonth = useCallback((month: number) => {
    const idx = plumeStates.findIndex(s => s.time_months === month);
    return idx >= 0 ? idx : 0;
  }, [plumeStates]);

  const [selectedIndex, setSelectedIndex] = useState(() => getIndexFromMonth(initialMonth || 0));
  const [internalVisibleLayers, setInternalVisibleLayers] = useState({
    psh: true,
    dissolved: true,
  });

  // Use external visibility state if provided, otherwise use internal
  const visibleLayers = externalVisibleLayers || internalVisibleLayers;
  const setVisibleLayers = onVisibleLayersChange || setInternalVisibleLayers;

  // Deferred loading: render base map immediately, mount heavy canvas overlays after first paint
  const [overlaysReady, setOverlaysReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOverlaysReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Get current state
  const currentState = plumeStates[selectedIndex] || plumeStates[0];
  const maxIndex = plumeStates.length - 1;

  // Handle manual index change
  const handleIndexChange = useCallback((newIndex: number) => {
    setSelectedIndex(newIndex);
    onMonthChange?.(plumeStates[newIndex]?.time_months || 0);
  }, [onMonthChange, plumeStates]);

  // Calculate site radius for coordinate conversion (uses max plume extent)
  // Include extracted boundaries if present for better scaling
  const siteRadiusFt = useMemo(() => {
    let maxRadius = plumeStates.reduce((max, s) => {
      const pshR = areaToRadius(s.psh?.area_sqft ?? 0);
      const disR = Math.max(s.dissolved.length_ft, s.dissolved.width_ft) / 2;
      return Math.max(pshR, disR, max);
    }, 500);

    // If we have extracted plume boundaries with absolute coordinates,
    // calculate radius from their extent
    if (extractedPlumeBoundaries && extractedPlumeBoundaries.length > 0) {
      for (const boundary of extractedPlumeBoundaries) {
        if (boundary.boundary_coords && boundary.boundary_coords.length > 2) {
          // Check if coordinates are absolute (not relative 0-1)
          if (!areCoordinatesRelative(boundary.boundary_coords)) {
            const lats = boundary.boundary_coords.map(c => c[0]);
            const lons = boundary.boundary_coords.map(c => c[1]);
            const latRange = Math.max(...lats) - Math.min(...lats);
            const lonRange = Math.max(...lons) - Math.min(...lons);
            // Convert degree range to feet (approx 364000 ft per degree lat)
            const latRangeFt = latRange * 364000;
            const lonRangeFt = lonRange * 364000 * Math.cos(centerLat * Math.PI / 180);
            const boundaryRadius = Math.max(latRangeFt, lonRangeFt) / 2;
            maxRadius = Math.max(maxRadius, boundaryRadius);
          }
        }
      }
    }

    // Add 50% buffer for gradient contours that may extend beyond plume
    return Math.max(maxRadius * 1.5, 1000);
  }, [plumeStates, extractedPlumeBoundaries, centerLat]);

  // Calculate bounds to fit all plumes
  const mapBounds = useMemo(() => {
    const buffer = feetToLatDegrees(siteRadiusFt);
    return [
      [centerLat - buffer, centerLon - buffer * 1.5],
      [centerLat + buffer, centerLon + buffer * 1.5],
    ] as L.LatLngBoundsExpression;
  }, [centerLat, centerLon, siteRadiusFt]);

  // ============================================================================
  // AGGREGATED WELL DATA: Build per-well contaminant summary from all boundaries
  // ============================================================================
  interface WellContaminantData {
    compound: string;
    concentration: number;  // µg/L
    mcl_ug_l: number;
    exceeds_mcl: boolean;
    is_non_detect: boolean;
  }

  interface AggregatedWell {
    well_id: string;
    lat: number;
    lon: number;
    contaminants: WellContaminantData[];
    has_any_exceedance: boolean;
    exceedance_count: number;
    total_compounds: number;
  }

  const aggregatedWells = useMemo((): AggregatedWell[] => {
    if (!wellBoundaries || wellBoundaries.length === 0) {
      return [];
    }

    // Build map of well_id -> aggregated data
    const wellMap = new Map<string, AggregatedWell>();

    // v3.2 horizon override: when the user picks a future horizon, swap each
    // well's measured concentration with the predicted value at that horizon
    // for the matching analyte. Predictions arrive in mg/L; the surrounding
    // code expects µg/L (boundaries are stored ug/L) — multiply by 1000.
    const useOverrides = !!wellHorizonOverrides && horizonDays > 0 && wellHorizonOverrides.size > 0;
    const lookupOverrideUgL = (wellId: string, compound: string): number | null => {
      if (!useOverrides) return null;
      const byAnalyte = wellHorizonOverrides!.get(wellId);
      if (!byAnalyte) return null;
      const mgl = byAnalyte.get(compound.toLowerCase());
      if (mgl == null || !Number.isFinite(mgl) || mgl < 0) return null;
      return mgl * 1000;  // mg/L → µg/L
    };

    /** Build one contaminant row, swapping in the v3.2 override when present.
     *  Returns null if the override produces a non-finite / negative value
     *  (defends against the "crazy numbers" bug). */
    const buildContaminant = (
      wellId: string,
      compound: string,
      measuredConcUgL: number,
      mcl: number,
      isNd: boolean,
    ): WellContaminantData | null => {
      const overrideUgL = lookupOverrideUgL(wellId, compound);
      const finalConc = overrideUgL != null ? overrideUgL : measuredConcUgL;
      if (!Number.isFinite(finalConc) || finalConc < 0) return null;
      // Override implies a forecast — the value is no longer a "non-detect"
      // even if the measured row was.
      const ndFlag = overrideUgL != null ? false : isNd;
      return {
        compound,
        concentration: finalConc,
        mcl_ug_l: mcl,
        exceeds_mcl: finalConc > mcl,
        is_non_detect: ndFlag,
      };
    };

    for (const boundary of wellBoundaries) {
      const compound = boundary.compound;
      const mcl = boundary.mcl_ug_l || 5.0;

      // Process wells with detections (above threshold)
      for (const well of (boundary.wells_with_detections || [])) {
        const existing = wellMap.get(well.well_id);
        const contaminantData = buildContaminant(
          well.well_id, compound, well.concentration, mcl, !!well.is_non_detect,
        );
        if (!contaminantData) continue;

        if (existing) {
          existing.contaminants.push(contaminantData);
          if (contaminantData.exceeds_mcl) {
            existing.has_any_exceedance = true;
            existing.exceedance_count++;
          }
          existing.total_compounds++;
        } else {
          wellMap.set(well.well_id, {
            well_id: well.well_id,
            lat: well.lat,
            lon: well.lon,
            contaminants: [contaminantData],
            has_any_exceedance: contaminantData.exceeds_mcl,
            exceedance_count: contaminantData.exceeds_mcl ? 1 : 0,
            total_compounds: 1,
          });
        }
      }

      // Process wells below threshold (may have detections but below MCL)
      for (const well of (boundary.wells_below_threshold || [])) {
        const existing = wellMap.get(well.well_id);
        const contaminantData = buildContaminant(
          well.well_id, compound, well.concentration, mcl, !!well.is_non_detect,
        );
        if (!contaminantData) continue;

        if (existing) {
          // Only add if this compound isn't already in the list
          const hasCompound = existing.contaminants.some(c => c.compound === compound);
          if (!hasCompound) {
            existing.contaminants.push(contaminantData);
            if (contaminantData.exceeds_mcl) {
              existing.has_any_exceedance = true;
              existing.exceedance_count++;
            }
            existing.total_compounds++;
          }
        } else {
          wellMap.set(well.well_id, {
            well_id: well.well_id,
            lat: well.lat,
            lon: well.lon,
            contaminants: [contaminantData],
            has_any_exceedance: contaminantData.exceeds_mcl,
            exceedance_count: contaminantData.exceeds_mcl ? 1 : 0,
            total_compounds: 1,
          });
        }
      }
    }

    // Convert map to array and sort contaminants by concentration (highest first)
    const wells = Array.from(wellMap.values());
    // Red-flag wells based ONLY on the compounds currently visible in the
    // MCL legend — same set the plume polygons render. So benzene-visible
    // mode reds wells exceeding benzene, ignoring exceedances on hidden
    // compounds. Keeps the well dots and the polygon in sync.
    const visibleSet = visibleCompounds && visibleCompounds.size > 0
      ? new Set(Array.from(visibleCompounds).map(c => c.toLowerCase()))
      : null;
    for (const well of wells) {
      well.contaminants.sort((a, b) => b.concentration - a.concentration);
      const relevant = visibleSet
        ? well.contaminants.filter(c => visibleSet.has(c.compound.toLowerCase()))
        : well.contaminants;
      const excCount = relevant.filter(c => c.exceeds_mcl).length;
      well.exceedance_count = excCount;
      well.has_any_exceedance = excCount > 0;
      well.total_compounds = well.contaminants.length;
    }

    return wells;
  }, [wellBoundaries, wellHorizonOverrides, horizonDays, visibleCompounds]);

  // Build PSH well lookup by well_id for tooltip integration
  const pshWellLookup = useMemo((): Map<string, PSHWellData> => {
    const map = new Map<string, PSHWellData>();
    if (pshWells && pshWells.length > 0) {
      for (const pw of pshWells) {
        map.set(pw.well_id, pw);
      }
    }
    return map;
  }, [pshWells]);

  // Build hotspot lookup by well_id for remediation glow + tooltip integration
  const hotspotLookup = useMemo(() => {
    const map = new Map<string, RemediationHotspot>();
    for (const h of (remediationHotspots || [])) map.set(h.well_id, h);
    return map;
  }, [remediationHotspots]);

  // ============================================================================
  // MEMOIZED WELL ICONS — avoid re-creating L.divIcon on every render
  // ============================================================================

  const makeWellIcon = (label: string, bg: string, size: [number, number] = [50, 20], extraStyle: string = '') =>
    L.divIcon({
      className: '',
      html: `<div style="
        background: ${bg};
        border: 2px solid #fff;
        border-radius: 4px;
        padding: 2px 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: white;
        font-size: 10px;
        white-space: nowrap;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ${extraStyle}
      ">${label}</div>`,
      iconSize: size,
      iconAnchor: [size[0] / 2, size[1] / 2],
    });

  const recoveryWellIcons = useMemo(() =>
    recoveryWells.map((w, idx) => {
      const wellName = w.name || w.id || `RW-${idx + 1}`;
      const isHotspot = hotspotLookup.has(wellName);
      const hasPSH = pshWellLookup.has(wellName);
      return makeWellIcon(
        wellName,
        hasPSH ? '#ef4444' : 'linear-gradient(135deg, #a3a3a3 0%, #737373 100%)',
        [50, 20],
        isHotspot ? 'animation: well-pulse 2s ease-in-out infinite;' : '',
      );
    }), [recoveryWells, hotspotLookup, pshWellLookup]);

  const mdpeWellIcons = useMemo(() =>
    mdpeWells.map((w, idx) => {
      const wellName = w.name || w.id || `MDPE-${idx + 1}`;
      const hasPSH = pshWellLookup.has(wellName);
      return makeWellIcon(
        wellName,
        hasPSH ? '#ef4444' : 'linear-gradient(135deg, #737373 0%, #525252 100%)',
        [60, 20],
      );
    }), [mdpeWells, pshWellLookup]);

  const aggregatedWellIcons = useMemo(() => {
    const icons = new Map<string, L.DivIcon>();
    for (const well of aggregatedWells) {
      const isHotspot = hotspotLookup.has(well.well_id);
      const hasPSH = pshWellLookup.has(well.well_id);
      icons.set(well.well_id, makeWellIcon(
        well.well_id,
        (well.has_any_exceedance || hasPSH) ? '#ef4444' : '#22c55e',
        [50, 20],
        isHotspot ? 'animation: well-pulse 2s ease-in-out infinite;' : '',
      ));
    }
    return icons;
  }, [aggregatedWells, hotspotLookup, pshWellLookup]);

  const monitoringWellIcons = useMemo(() => {
    const icons = new Map<string, L.DivIcon>();
    for (const well of monitoringWells) {
      const isHotspot = hotspotLookup.has(well.well_id);
      const hasPSH = pshWellLookup.has(well.well_id);
      icons.set(well.well_id, makeWellIcon(
        well.well_id,
        hasPSH ? '#ef4444' : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
        [50, 20],
        isHotspot ? 'animation: well-pulse 2s ease-in-out infinite;' : '',
      ));
    }
    return icons;
  }, [monitoringWells, hotspotLookup, pshWellLookup]);

  const exceedanceWellIcons = useMemo(() => {
    const icons = new Map<string, L.DivIcon>();
    for (const well of wellsAboveThreshold) {
      icons.set(well.well_id, makeWellIcon(well.well_id, '#ef4444'));
    }
    return icons;
  }, [wellsAboveThreshold]);

  // Validate inputs
  if (!plumeStates || plumeStates.length === 0) {
    return (
      <div className="w-full h-[650px] bg-mono-950 rounded-lg flex items-center justify-center border border-mono-700">
        <p className="text-mono-500 text-sm">No three-plume data available</p>
      </div>
    );
  }

  const { psh, dissolved } = currentState;


  return (
    <div className="w-full rounded-lg overflow-hidden border border-mono-700 bg-mono-950">
      {/* Subtle pulse glow for wells with remediation recommendations */}
      <style>{`
        @keyframes well-pulse {
          0%, 100% { box-shadow: 0 2px 4px rgba(0,0,0,0.3); }
          50% { box-shadow: 0 0 8px 3px rgba(239, 68, 68, 0.5); }
        }
      `}</style>
      {/* Header with layer toggles */}
      <div className="bg-gradient-to-r from-mono-900 via-mono-850 to-mono-900 p-3 border-b border-mono-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-mono-300 uppercase tracking-wider">
              Three-Plume Model
            </span>
            <span className="text-xs text-mono-500">|</span>
            <span className="text-xs text-cyan-400 font-mono">
              Month {currentState.time_months}
            </span>
          </div>

          {/* Layer toggles with color indicators */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-mono-500 uppercase mr-2">Layers:</span>
            <button
              onClick={() => setVisibleLayers({ ...visibleLayers, psh: !visibleLayers.psh })}
              className={`px-2 py-1 text-xs rounded flex items-center gap-1.5 transition-all ${
                visibleLayers.psh
                  ? 'bg-amber-900/40 text-amber-400 border border-amber-600/50'
                  : 'bg-mono-800 text-mono-500 border border-mono-700'
              }`}
            >
              <div className="w-3 h-3 rounded-full" style={{
                background: `linear-gradient(135deg, ${PSH_COLORS.core} 0%, ${PSH_COLORS.outer} 100%)`
              }}></div>
              PSH
            </button>
            <button
              onClick={() => setVisibleLayers({ ...visibleLayers, dissolved: !visibleLayers.dissolved })}
              className={`px-2 py-1 text-xs rounded flex items-center gap-1.5 transition-all ${
                visibleLayers.dissolved
                  ? 'bg-cyan-900/40 text-cyan-400 border border-cyan-600/50'
                  : 'bg-mono-800 text-mono-500 border border-mono-700'
              }`}
            >
              <div className="w-3 h-3 rounded-full" style={{
                background: `linear-gradient(135deg, ${DISSOLVED_COLORS.core} 0%, ${DISSOLVED_COLORS.outer} 100%)`
              }}></div>
              Dissolved
            </button>
            {/* Gradient contours toggle */}
            {gradientMap && onGradientContoursToggle && (
              <button
                onClick={() => onGradientContoursToggle(!showGradientContours)}
                className={`px-2 py-1 text-xs rounded flex items-center gap-1.5 transition-all ${
                  showGradientContours
                    ? 'bg-blue-900/40 text-blue-400 border border-blue-600/50'
                    : 'bg-mono-800 text-mono-500 border border-mono-700'
                }`}
              >
                <div className="w-3 h-3 rounded-full" style={{
                  background: 'linear-gradient(135deg, #0066cc 0%, #003366 100%)'
                }}></div>
                Contours
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <MapContainer
        center={[centerLat, centerLon]}
        zoom={16}
        style={{ height: '650px', width: '100%' }}
        scrollWheelZoom={false}
      >
        <CtrlScrollZoom />
        <EsriVectorBasemap />

        <FitBounds bounds={mapBounds} />
        <MapCompass />
        <MapScaleBar />

        {/* Gradient contour layer (behind plumes) */}
        {overlaysReady && gradientMap && (
          <GradientContourLayer
            contours={gradientMap.contours || []}
            flowArrows={gradientMap.flow_arrows}
            wellReadings={gradientMap.well_readings}
            visible={showGradientContours}
            showLabels={true}
            showFlowArrows={true}
            showWellReadings={true}
            labelInterval={2}
            siteCenterLat={centerLat}
            siteCenterLon={centerLon}
            siteRadiusFt={siteRadiusFt}
            coordinatesType={gradientMap.coordinates_type}
            mapNorthDirection={gradientMap.map_north_direction}
          />
        )}

        {/* Extracted plume boundaries (from AGMR - shown at T=0 as reference) */}
        {/* Hidden when particle plumes are showing */}
        {showExtractedBoundaries && extractedPlumeBoundaries && extractedPlumeBoundaries.length > 0 && currentState.time_months === 0 && !(showMultiContaminant && wellBoundaries && wellBoundaries.length > 0) && (
          <>
            {extractedPlumeBoundaries.map((boundary, idx) => {
              // Only render if we have valid coordinates
              if (!boundary.boundary_coords || boundary.boundary_coords.length < 3) return null;

              // Convert coordinates if they're in relative 0-1 range, and apply rotation
              const convertedCoords = convertBoundaryCoords(
                boundary.boundary_coords,
                centerLat,
                centerLon,
                siteRadiusFt,
                gradientMap?.map_north_direction
              );

              // Get color based on concentration level (darker = higher concentration)
              const maxConc = Math.max(...extractedPlumeBoundaries.map(b => b.concentration_ug_l || 0));
              const concRatio = maxConc > 0 ? (boundary.concentration_ug_l || 0) / maxConc : 0.5;
              const opacity = 0.2 + (concRatio * 0.4);

              return (
                <Polygon
                  key={`extracted-boundary-${idx}-${boundary.concentration_ug_l}`}
                  positions={convertedCoords}
                  pathOptions={{
                    color: '#dc2626', // Red outline for extracted boundaries
                    fillColor: '#fca5a5', // Light red fill
                    fillOpacity: opacity,
                    weight: 2,
                    dashArray: '6, 4', // Dashed to indicate it's extracted/reference data
                  }}
                >
                  <Tooltip direction="center" permanent={false}>
                    <div className="text-xs">
                      <div className="font-medium text-red-600">Extracted Boundary</div>
                      <div>Concentration: {((boundary.concentration_ug_l || 0) / 1000).toFixed(3)} mg/L</div>
                    </div>
                  </Tooltip>
                </Polygon>
              );
            })}
          </>
        )}

        {/* Render plumes in order: Dissolved (back), PSH (front) */}

        {/* Multi-Contaminant Plume Layer (voxel grid - BTEX) */}
        {/* Renders if we have simulation results OR well-measured boundaries */}
        {overlaysReady && showMultiContaminant && ((contaminantResults && contaminantResults.length > 0) || (wellBoundaries && wellBoundaries.length > 0)) && (
          <MultiContaminantPlumeLayer
            centerLat={centerLat}
            centerLon={centerLon}
            baseLengthFt={dissolved.length_ft}
            baseWidthFt={dissolved.width_ft}
            flowDirection={flowDirection}
            contaminantResults={contaminantResults}
            primaryContaminant="benzene"
            visible={visibleLayers.dissolved}
            gradientMap={gradientMap}
            recoveryWells={recoveryWells}
            selectedTimeIndex={selectedIndex}
            visibleCompounds={visibleCompounds}
            wellBoundaries={wellBoundaries}
            predictedWellConcentrations={predictedWellConcentrations}
            predictionDate={predictionDate}
          />
        )}

        {/* PSH Plume - Voxel canvas with real well data */}
        {overlaysReady && pshWells.length > 0 && (
          <PSHCanvasLayer
            wells={pshWells}
            visible={visibleLayers.psh}
            gridResolution={160}
            baseOpacity={0.9}
            zIndex={400}
          />
        )}

        {/* Source marker (always on top) */}
        <Marker position={[centerLat, centerLon]} icon={sourceIcon} zIndexOffset={1000} />

        {/* Recovery well markers */}
        {recoveryWells.map((well, idx) => (
          well.latitude && well.longitude ? (
            <Marker
              key={well.id || `recovery-${well.x_ft}-${well.y_ft}`}
              position={[well.latitude, well.longitude]}
              icon={recoveryWellIcons[idx]}
              zIndexOffset={800}
              eventHandlers={{
                click: () => {
                  if (onWellRemoved && well.id) {
                    // Show confirmation or remove
                    if (confirm(`Remove recovery well? (${well.pump_rate_gpd.toLocaleString()} GPD)`)) {
                      onWellRemoved('recovery', well.id);
                    }
                  }
                },
              }}
            >
              <Tooltip permanent={false} direction="top" offset={[0, -10]}>
                <div className="text-xs">
                  <div className="font-semibold">{well.name || well.id || `RW-${idx + 1}`}</div>
                  <div className="text-mono-600">Pump Rate: {well.pump_rate_gpd.toLocaleString()} GPD</div>
                  <div className="text-mono-500">Start Month: {well.start_month}</div>
                  <div className="text-mono-400 text-[10px]">Recovery Well</div>
                  {(() => {
                    const rwName = well.name || well.id || `RW-${idx + 1}`;
                    const h = hotspotLookup.get(rwName);
                    if (!h) return null;
                    return (
                      <div style={{ borderTop: '1px solid #fca5a5', marginTop: 4, paddingTop: 4 }}>
                        <div style={{ fontWeight: 'bold', color: '#dc2626', fontSize: 10 }}>
                          Remediation Recommended
                        </div>
                        {h.psh_thickness_ft != null && (
                          <div style={{ fontSize: 10 }}>PSH: {h.psh_thickness_ft.toFixed(2)} ft (median {h.psh_site_median_ft?.toFixed(2)})</div>
                        )}
                        {h.dissolved_max_conc_ugl != null && (
                          <div style={{ fontSize: 10 }}>{h.dissolved_compound}: {(h.dissolved_max_conc_ugl/1000).toFixed(3)} mg/L (MCL {(h.dissolved_mcl_ugl!/1000).toFixed(3)})</div>
                        )}
                        <div style={{ fontSize: 9, color: '#6b7280' }}>Severity: {h.severity}</div>
                      </div>
                    );
                  })()}
                </div>
              </Tooltip>
            </Marker>
          ) : null
        ))}

        {/* MDPE well markers */}
        {mdpeWells.map((well, idx) => (
          well.latitude && well.longitude ? (
            <Marker
              key={well.id || `mdpe-${well.x_ft}-${well.y_ft}`}
              position={[well.latitude, well.longitude]}
              icon={mdpeWellIcons[idx]}
              zIndexOffset={800}
              eventHandlers={{
                click: () => {
                  if (onWellRemoved && well.id) {
                    // Show confirmation or remove
                    if (confirm(`Remove MDPE well? (${well.extraction_cfm} CFM)`)) {
                      onWellRemoved('mdpe', well.id);
                    }
                  }
                },
              }}
            >
              <Tooltip permanent={false} direction="top" offset={[0, -10]}>
                <div className="text-xs">
                  <div className="font-semibold">{well.name || well.id || `MDPE-${idx + 1}`}</div>
                  <div className="text-mono-600">Extraction: {well.extraction_cfm} CFM</div>
                  <div className="text-mono-500">Start Month: {well.start_month}</div>
                  <div className="text-mono-400 text-[10px]">MDPE Well</div>
                </div>
              </Tooltip>
            </Marker>
          ) : null
        ))}

        {/* MDPE well radius of influence circles */}
        {mdpeWells.map((well) => {
          if (!well.latitude || !well.longitude) return null;

          // Calculate ROI based on extraction rate
          const cfmFactor = Math.min(well.extraction_cfm / 100, 3); // Normalize to typical 100 CFM
          const roiFt = 50 * cfmFactor; // Base 50 ft ROI
          const roiDeg = roiFt / 364000;

          // Generate circle coordinates
          const circleCoords: [number, number][] = [];
          for (let angle = 0; angle < 360; angle += 15) {
            const rad = (angle * Math.PI) / 180;
            circleCoords.push([
              well.latitude + Math.sin(rad) * roiDeg,
              well.longitude + Math.cos(rad) * roiDeg / Math.cos(well.latitude * Math.PI / 180),
            ]);
          }

          return (
            <Polygon
              key={`roi-${well.id}`}
              positions={circleCoords}
              pathOptions={{
                color: '#22c55e',
                fillColor: '#22c55e',
                fillOpacity: 0.15,
                weight: 2,
                dashArray: '4, 4',
              }}
            />
          );
        })}

        {/* ================================================================== */}
        {/* AGGREGATED WELL MARKERS - Show well label, details on hover */}
        {/* ================================================================== */}
        {aggregatedWells.map((well) => (
          <Marker
            key={`agg-well-${well.well_id}`}
            position={[well.lat, well.lon]}
            icon={aggregatedWellIcons.get(well.well_id)!}
            zIndexOffset={850}
          >
            <Tooltip permanent={false} direction="top" offset={[0, -12]}>
              <div style={{ minWidth: '220px', maxWidth: '300px' }}>
                {/* Well ID header */}
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '13px',
                  color: well.has_any_exceedance ? '#dc2626' : '#16a34a',
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: '4px',
                  marginBottom: '6px',
                }}>
                  {well.well_id}
                  {well.has_any_exceedance && (
                    <span style={{ marginLeft: '8px', fontSize: '10px', color: '#dc2626' }}>
                      ({well.exceedance_count} exceedance{well.exceedance_count !== 1 ? 's' : ''})
                    </span>
                  )}
                </div>

                {/* PSH / Gauging Data section */}
                {(() => {
                  const pshData = pshWellLookup.get(well.well_id);
                  if (!pshData) return null;
                  return (
                    <div style={{ marginBottom: '6px' }}>
                      <div style={{
                        fontWeight: 'bold',
                        fontSize: '11px',
                        color: '#b45309',
                        marginBottom: '3px',
                      }}>
                        PSH / Gauging
                      </div>
                      <div style={{ fontSize: '11px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                          <span style={{ color: '#374151' }}>Product Thickness</span>
                          <span style={{ fontFamily: 'monospace', fontSize: '10px', color: pshData.product_thickness_ft > 0 ? '#b45309' : '#6b7280', marginLeft: '12px' }}>
                            {pshData.product_thickness_ft.toFixed(2)} ft
                          </span>
                        </div>
                        {pshData.depth_to_water_ft != null && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                            <span style={{ color: '#374151' }}>Depth to Water</span>
                            <span style={{ fontFamily: 'monospace', fontSize: '10px', color: '#6b7280', marginLeft: '12px' }}>
                              {pshData.depth_to_water_ft.toFixed(2)} ft
                            </span>
                          </div>
                        )}
                        {pshData.depth_to_product_ft != null && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                            <span style={{ color: '#374151' }}>Depth to Product</span>
                            <span style={{ fontFamily: 'monospace', fontSize: '10px', color: '#6b7280', marginLeft: '12px' }}>
                              {pshData.depth_to_product_ft.toFixed(2)} ft
                            </span>
                          </div>
                        )}
                      </div>
                      <div style={{ borderBottom: '1px solid #e5e7eb', marginTop: '4px', marginBottom: '4px' }} />
                    </div>
                  );
                })()}

                {/* Dissolved Contaminant list */}
                {well.contaminants.length > 0 && (
                  <div style={{
                    fontWeight: 'bold',
                    fontSize: '11px',
                    color: '#2563eb',
                    marginBottom: '3px',
                  }}>
                    Dissolved
                  </div>
                )}
                <div style={{ fontSize: '11px' }}>
                  {well.contaminants.map((contam, idx) => (
                    <div
                      key={`${well.well_id}-${contam.compound}-${idx}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '2px 0',
                        borderBottom: idx < well.contaminants.length - 1 ? '1px solid #f3f4f6' : 'none',
                      }}
                    >
                      <span style={{
                        fontWeight: contam.exceeds_mcl ? 'bold' : 'normal',
                        color: contam.exceeds_mcl ? '#dc2626' : contam.is_non_detect ? '#9ca3af' : '#374151',
                      }}>
                        {contam.exceeds_mcl ? '⚠ ' : ''}{contam.compound}
                        {contam.is_non_detect && <span style={{ color: '#9ca3af', fontStyle: 'italic' }}> (ND)</span>}
                      </span>
                      <span style={{
                        fontFamily: 'monospace',
                        fontSize: '10px',
                        color: contam.exceeds_mcl ? '#dc2626' : '#6b7280',
                        marginLeft: '12px',
                      }}>
                        {/* Sanitize against the legacy "crazy numbers" bug at
                            future months. log10-space values, NaN, or absurdly
                            large floats used to leak through here as e.g.
                            "1.234e308 mg/L". Filter to finite non-negative and
                            switch to exponential notation past 999 mg/L. */}
                        {formatConcMgL(contam.concentration / 1000)} / {formatConcMgL(contam.mcl_ug_l / 1000)} mg/L
                      </span>
                    </div>
                  ))}
                </div>

                {/* Summary footer */}
                <div style={{
                  marginTop: '6px',
                  paddingTop: '4px',
                  borderTop: '1px solid #e5e7eb',
                  fontSize: '10px',
                  color: '#6b7280',
                }}>
                  {well.total_compounds} compound{well.total_compounds !== 1 ? 's' : ''} analyzed
                  {well.has_any_exceedance && (
                    <span style={{ color: '#dc2626', marginLeft: '8px' }}>
                      {well.exceedance_count} above MCL
                    </span>
                  )}
                </div>

                {/* Remediation recommendation (from hotspot detection) */}
                {hotspotLookup.has(well.well_id) && (() => {
                  const h = hotspotLookup.get(well.well_id)!;
                  return (
                    <div style={{ borderTop: '1px solid #fca5a5', marginTop: 4, paddingTop: 4 }}>
                      <div style={{ fontWeight: 'bold', color: '#dc2626', fontSize: 10 }}>
                        Remediation Recommended
                      </div>
                      {h.psh_thickness_ft != null && (
                        <div style={{ fontSize: 10 }}>PSH: {h.psh_thickness_ft.toFixed(2)} ft (median {h.psh_site_median_ft?.toFixed(2)})</div>
                      )}
                      {h.dissolved_max_conc_ugl != null && (
                        <div style={{ fontSize: 10 }}>{h.dissolved_compound}: {formatConcMgL(h.dissolved_max_conc_ugl/1000)} mg/L (MCL {formatConcMgL((h.dissolved_mcl_ugl ?? 0)/1000)})</div>
                      )}
                      <div style={{ fontSize: 9, color: '#6b7280' }}>Severity: {h.severity}</div>
                    </div>
                  );
                })()}
              </div>
            </Tooltip>
          </Marker>
        ))}

        {/* Monitoring well markers (only show if no aggregated data) */}
        {aggregatedWells.length === 0 && monitoringWells.map((well) => (
          well.latitude && well.longitude ? (
            <Marker
              key={well.id || `mw-${well.well_id}`}
              position={[well.latitude, well.longitude]}
              icon={monitoringWellIcons.get(well.well_id)!}
              zIndexOffset={600}
            >
              <Tooltip permanent={false} direction="top" offset={[0, -10]}>
                <div className="text-xs">
                  <div className="font-semibold">{well.well_id}</div>
                  {well.depth_ft && <div className="text-mono-500">Depth: {well.depth_ft} ft</div>}
                  <div className="text-mono-400 text-[10px]">Monitoring Well</div>
                  {(() => {
                    const h = hotspotLookup.get(well.well_id);
                    if (!h) return null;
                    return (
                      <div style={{ borderTop: '1px solid #fca5a5', marginTop: 4, paddingTop: 4 }}>
                        <div style={{ fontWeight: 'bold', color: '#dc2626', fontSize: 10 }}>
                          Remediation Recommended
                        </div>
                        {h.psh_thickness_ft != null && (
                          <div style={{ fontSize: 10 }}>PSH: {h.psh_thickness_ft.toFixed(2)} ft (median {h.psh_site_median_ft?.toFixed(2)})</div>
                        )}
                        {h.dissolved_max_conc_ugl != null && (
                          <div style={{ fontSize: 10 }}>{h.dissolved_compound}: {(h.dissolved_max_conc_ugl/1000).toFixed(3)} mg/L (MCL {(h.dissolved_mcl_ugl!/1000).toFixed(3)})</div>
                        )}
                        <div style={{ fontSize: 9, color: '#6b7280' }}>Severity: {h.severity}</div>
                      </div>
                    );
                  })()}
                </div>
              </Tooltip>
            </Marker>
          ) : null
        ))}

        {/* Legacy exceedance markers (only show if no aggregated data) */}
        {aggregatedWells.length === 0 && wellsAboveThreshold.map((well) => (
          <Marker
            key={`exceedance-${well.well_id}`}
            position={[well.lat, well.lon]}
            icon={exceedanceWellIcons.get(well.well_id)!}
            zIndexOffset={900}
          >
            <Tooltip permanent={false} direction="top" offset={[0, -10]}>
              <div className="text-xs font-medium">
                <div className="text-red-600 font-semibold">{well.well_id}</div>
                <div className="text-mono-600">
                  {(well.concentration / 1000).toFixed(3)} mg/L
                </div>
                <div className="text-red-500 text-[10px]">Exceeds threshold</div>
              </div>
            </Tooltip>
          </Marker>
        ))}

        {/* DW (Delineation Well) markers removed from 2D map — moved to the
            dedicated Delineation Intelligence tab. Keep the dwRecommendations
            prop wired so future overlays (e.g. minimal dots) can be added. */}


        {/* Map click handler for well placement */}
        {onWellPlaced && (
          <MapClickHandler
            mode={wellPlacementMode || null}
            onPlace={(lat, lon) => {
              if (wellPlacementMode && onWellPlaced) {
                onWellPlaced(wellPlacementMode, lat, lon);
              }
            }}
          />
        )}
      </MapContainer>

      {/* Well Placement Mode Indicator */}
      {wellPlacementMode && (
        <div className="p-3 border-t bg-mono-800/50 border-mono-700">
          <div className="flex items-center justify-center gap-2">
            <div className="w-3 h-3 rounded-full animate-pulse bg-mono-400" />
            <span className="text-xs font-medium text-mono-300">
              Click on map to place {wellPlacementMode === 'recovery' ? 'Recovery Well' : 'MDPE Well'}
            </span>
          </div>
        </div>
      )}

      {/* Legend with gradient indicators */}
      <div className="bg-gradient-to-r from-mono-900 via-mono-850 to-mono-900 p-3 border-t border-mono-700">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          {/* Source */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-br from-red-500 to-red-700 rounded-full border-2 border-white"></div>
            <span className="text-mono-300">Source Zone</span>
          </div>

          {/* PSH gradient legend — reads left→right: low → high (dark on right) */}
          <div className="flex items-center gap-2 border-l border-mono-700 pl-4">
            <span className="text-mono-500 text-[10px]">low</span>
            <div className="flex h-3 rounded overflow-hidden">
              <div className="w-3" style={{ background: PSH_COLORS.outer }}></div>
              <div className="w-3" style={{ background: PSH_COLORS.low }}></div>
              <div className="w-3" style={{ background: PSH_COLORS.medium }}></div>
              <div className="w-3" style={{ background: PSH_COLORS.high }}></div>
              <div className="w-3" style={{ background: PSH_COLORS.core }}></div>
            </div>
            <span className="text-mono-500 text-[10px]">high</span>
            <span className="text-amber-400">PSH/LNAPL</span>
          </div>

          {/* Dissolved gradient legend — reads left→right: low → high (dark on right) */}
          <div className="flex items-center gap-2 border-l border-mono-700 pl-4">
            <span className="text-mono-500 text-[10px]">low</span>
            <div className="flex h-3 rounded overflow-hidden">
              <div className="w-3" style={{ background: DISSOLVED_COLORS.outer }}></div>
              <div className="w-3" style={{ background: DISSOLVED_COLORS.low }}></div>
              <div className="w-3" style={{ background: DISSOLVED_COLORS.medium }}></div>
              <div className="w-3" style={{ background: DISSOLVED_COLORS.high }}></div>
              <div className="w-3" style={{ background: DISSOLVED_COLORS.core }}></div>
            </div>
            <span className="text-mono-500 text-[10px]">high</span>
            <span className="text-cyan-400">Dissolved</span>
          </div>

          {/* DW legend removed — recommendations live on the Delineation Intel tab */}


        </div>
      </div>
    </div>
  );
}

export default ThreePlumeMap;
